/** 现场「要求完成」日历日截止时刻（本地时区） */
export const DISPATCH_DUE_END_HOUR = 22

/** 本地日历日零点 */
function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** deadline 相对 ref 的日历天数差（负表示已过期） */
export function calendarDaysBetween(deadline, ref) {
  const a = startOfLocalDay(deadline).getTime()
  const b = startOfLocalDay(ref).getTime()
  return Math.round((a - b) / 86400000)
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

export function formatDueDateOnly(isoOrDate) {
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (!d || Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** 实际日期时间（如 warehouse_completed_at 现场提交时刻，不做 22:00 归一） */
export function formatDueDateTime(isoOrDate) {
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate
  if (!d || Number.isNaN(d.getTime())) return ''
  return `${formatDueDateOnly(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

/** 「要求完成」展示：所选日历日的 22:00 截止 */
export function formatRequiredCompletionDeadline(isoOrDate) {
  const deadline = dispatchDueDeadline(isoOrDate)
  if (!deadline) return ''
  return `${formatDueDateOnly(deadline)} ${pad2(deadline.getHours())}:${pad2(deadline.getMinutes())}`
}

/**
 * 将要求完成时间规范为所选日历日的 22:00（保存/API 提交用）。
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export function normalizeRequiredCompletionAtForSave(value) {
  if (value == null || String(value).trim() === '') return null
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s}T${pad2(DISPATCH_DUE_END_HOUR)}:00:00`
  }
  const deadline = dispatchDueDeadline(s)
  if (!deadline) return s
  return `${deadline.getFullYear()}-${pad2(deadline.getMonth() + 1)}-${pad2(deadline.getDate())}T${pad2(DISPATCH_DUE_END_HOUR)}:00:00`
}

/**
 * 要求完成截止时刻：所选日期的当天 22:00（非 0 点）。
 * @param {string|Date|null|undefined} isoOrDate
 * @returns {Date|null}
 */
export function dispatchDueDeadline(isoOrDate) {
  if (isoOrDate == null || isoOrDate === '') return null
  let d
  if (isoOrDate instanceof Date) {
    d = isoOrDate
  } else {
    const s = String(isoOrDate).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, day] = s.split('-').map(Number)
      d = new Date(y, m - 1, day)
    } else {
      d = new Date(s)
    }
  }
  if (!d || Number.isNaN(d.getTime())) return null
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), DISPATCH_DUE_END_HOUR, 0, 0, 0)
}

/**
 * 仓库已点「完成并提交业务审查」后：以现场完成时刻为准判断是否超要求完成日，
 * 避免待核销期间仍用「当前时间」累加逾期天数。
 */
function classifyDispatchDueAfterWarehouseComplete(completion, immediate, due, hasValidDue) {
  const doneStr = formatDueDateTime(completion)

  if (immediate && !hasValidDue) {
    return {
      kind: 'completed_ok',
      label: '已完成',
      tagType: 'success',
      dateLine: `特急单 · 现场完成 ${doneStr}`,
      lineClass: 'm-due--completed-ok'
    }
  }

  if (!hasValidDue) {
    return {
      kind: 'completed_ok',
      label: '已完成',
      tagType: 'success',
      dateLine: `现场完成 ${doneStr}`,
      lineClass: 'm-due--completed-ok'
    }
  }

  const dueDeadline = dispatchDueDeadline(due)
  const calDiff = calendarDaysBetween(dueDeadline, completion)
  const msLate = completion.getTime() - dueDeadline.getTime()

  if (msLate > 0) {
    if (calDiff < 0) {
      const overdueDays = Math.abs(calDiff)
      return {
        kind: 'completed_late',
        label: '超时完成',
        tagType: 'danger',
        dateLine: `已超期 ${overdueDays} 天 · 要求 ${formatRequiredCompletionDeadline(due)} · 现场完成 ${doneStr}`,
        lineClass: 'm-due--overdue'
      }
    }
    const hoursLate = Math.max(1, Math.ceil(msLate / 3600000))
    return {
      kind: 'completed_late',
      label: '超时完成',
      tagType: 'danger',
      dateLine: `当日超时 · 超期约 ${hoursLate} 小时 · 要求 ${formatRequiredCompletionDeadline(due)} · 现场完成 ${doneStr}`,
      lineClass: 'm-due--overdue'
    }
  }

  if (calDiff > 0) {
    return {
      kind: 'completed_ok',
      label: '正常',
      tagType: 'success',
      dateLine: `提前完成 · 要求 ${formatRequiredCompletionDeadline(due)} · 现场完成 ${doneStr}`,
      lineClass: 'm-due--completed-ok'
    }
  }

  return {
    kind: 'completed_ok',
    label: '正常',
    tagType: 'success',
    dateLine: `按时完成 · 要求 ${formatRequiredCompletionDeadline(due)} · 现场完成 ${doneStr}`,
    lineClass: 'm-due--completed-ok'
  }
}

/**
 * 派工单要求完成时间、相对剩余说明与特级（马上完成）标识。
 * @returns {{
 *   kind: 'none'|'critical'|'overdue'|'today'|'future'|'completed_ok'|'completed_late',
 *   label: string,
 *   tagType: string,
 *   dateLine: string,
 *   lineClass: string
 * }}
 */
export function classifyDispatchDue(row, now = new Date()) {
  const completionIso = row?.warehouse_completed_at
  const completion = completionIso ? new Date(completionIso) : null
  const hasValidCompletion = completion && !Number.isNaN(completion.getTime())

  const immediate = Boolean(row?.required_completion_immediate)
  const iso = row?.required_completion_at
  const due = iso ? new Date(iso) : null
  const hasValidDue = due && !Number.isNaN(due.getTime())

  if (hasValidCompletion) {
    return classifyDispatchDueAfterWarehouseComplete(completion, immediate, due, hasValidDue)
  }

  if (immediate) {
    const tail = hasValidDue ? ` · 截止 ${formatRequiredCompletionDeadline(due)}` : ''
    return {
      kind: 'critical',
      label: '特级',
      tagType: 'danger',
      dateLine: `须尽快完成${tail}`,
      lineClass: 'm-due--critical'
    }
  }
  if (!hasValidDue) {
    return { kind: 'none', label: '', tagType: 'info', dateLine: '', lineClass: '' }
  }

  const dueDeadline = dispatchDueDeadline(due)
  const diff = calendarDaysBetween(dueDeadline, now)
  const msLeft = dueDeadline.getTime() - now.getTime()

  if (msLeft < 0) {
    const overdueDays = diff < 0 ? Math.abs(diff) : 0
    const dayPart =
      overdueDays > 0
        ? `已超期 ${overdueDays} 天`
        : `已超过今日 ${DISPATCH_DUE_END_HOUR}:00 截止`
    return {
      kind: 'overdue',
      label: '已逾期',
      tagType: 'danger',
      dateLine: `${dayPart} · 要求 ${formatRequiredCompletionDeadline(due)}`,
      lineClass: 'm-due--overdue'
    }
  }

  if (diff === 0) {
    const hoursLeft = msLeft / 3600000
    let rel = '须今日内完成'
    if (hoursLeft < 1) rel = `须今日 ${DISPATCH_DUE_END_HOUR}:00 前完成 · 剩余不足 1 小时`
    else if (hoursLeft < 24) {
      rel = `须今日 ${DISPATCH_DUE_END_HOUR}:00 前完成 · 剩余约 ${Math.ceil(hoursLeft)} 小时`
    }
    return {
      kind: 'today',
      label: '今日',
      tagType: 'warning',
      dateLine: `${rel} · ${formatRequiredCompletionDeadline(due)}`,
      lineClass: 'm-due--today'
    }
  }

  return {
    kind: 'future',
    label: diff <= 2 ? '近日' : '充裕',
    tagType: 'success',
    dateLine: `剩余 ${diff} 天 · 截至 ${formatDueDateOnly(due)} ${DISPATCH_DUE_END_HOUR}:00`,
    lineClass: 'm-due--future'
  }
}
